import React from 'react';
import { List, MagnifyingGlass, SquaresFour } from '@phosphor-icons/react';
import { eur } from '../../../lib/immobilien/format';
import { BUCHHALTUNG_FOLGT } from '../../../lib/immobilien/hinweise';
import { RUECKSTAND_SCHWELLE } from '../../../lib/mietkonto/mietkonto';

/**
 * Gemeinsame Bausteine der Personenlisten (Mieter, Eigentümer): Avatar mit Portal-Punkt,
 * 12 Monats-Pips, Kontostand-Anzeige, Ansicht-Umschalter, Suchfeld und Anteils-Ring.
 */

export type AvatarTon = 'acc' | 'info' | 'bad' | 'grau';
const AVATAR: Record<AvatarTon, { bg: string; fg: string }> = {
  acc: { bg: 'var(--acc-soft)', fg: 'var(--acc)' },
  info: { bg: 'var(--info-soft)', fg: 'var(--info)' },
  bad: { bg: 'var(--bad-soft)', fg: 'var(--bad)' },
  grau: { bg: 'var(--fill2)', fg: 'var(--ink3)' },
};

/** Avatar mit Initialen; bei 48 px optional ein Punkt (z. B. Portalstatus). */
export function PersonAvatar({ text, ton = 'acc', groesse = 48, punkt }: {
  text: string;
  ton?: AvatarTon;
  groesse?: 32 | 48;
  punkt?: { farbe: string; label: string };
}) {
  const f = AVATAR[ton];
  return (
    <div className="relative shrink-0">
      <span className="flex items-center justify-center"
        style={{ width: groesse, height: groesse, borderRadius: '50%', fontSize: groesse === 48 ? 15 : 11, fontWeight: 800, background: f.bg, color: f.fg }}>
        {text}
      </span>
      {groesse === 48 && punkt && (
        <span title={punkt.label} aria-label={punkt.label}
          style={{ position: 'absolute', right: -1, bottom: -1, width: 14, height: 14, borderRadius: '50%', background: punkt.farbe, boxShadow: '0 0 0 3px var(--bg)' }} />
      )}
    </div>
  );
}

export type PipFarbe = 'ok' | 'warn' | 'bad' | 'vor';
const PIP_FARBE: Record<PipFarbe, string> = { ok: 'var(--ok)', warn: 'var(--warn)', bad: 'var(--bad)', vor: 'var(--fill2)' };

/** 12 Monats-Pips; ohne Daten (null) grau mit Hinweis „Startet mit der Buchhaltung“. */
export function MonatsPips({ pips }: { pips: readonly { monat: string; label: string; farbe: PipFarbe }[] | null }) {
  return (
    <div className="flex flex-1" style={{ gap: 3 }} title={pips ? undefined : BUCHHALTUNG_FOLGT} aria-hidden={!pips}>
      {pips
        ? pips.map(p => <span key={p.monat} title={p.label} style={{ flex: 1, height: 6, borderRadius: 3, background: PIP_FARBE[p.farbe] }} />)
        : Array.from({ length: 12 }, (_, i) => <span key={i} style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--fill2)' }} />)}
    </div>
  );
}

/** Kontostand: Rückstand rot, sonst „Ausgeglichen“ bzw. Guthaben; ohne Konto „–“. */
export function saldoAnzeige(saldo: number | null): { text: string; farbe: string; titel?: string; rueckstand: boolean } {
  if (saldo === null) return { text: '–', farbe: 'var(--ink3)', titel: BUCHHALTUNG_FOLGT, rueckstand: false };
  if (saldo < -RUECKSTAND_SCHWELLE) return { text: eur(saldo), farbe: 'var(--bad)', rueckstand: true };
  if (saldo > RUECKSTAND_SCHWELLE) return { text: eur(saldo, { vorzeichen: true }), farbe: 'var(--ok)', titel: 'Guthaben', rueckstand: false };
  return { text: 'Ausgeglichen', farbe: 'var(--ok)', rueckstand: false };
}

export type ListenAnsicht = 'kacheln' | 'liste';

/** Ansicht-Umschalter Kacheln/Liste (38×34, Radius 17), ganz rechts in der Filterzeile. */
export function AnsichtUmschalter({ wert, onChange, hoehe = 34 }: { wert: ListenAnsicht; onChange: (a: ListenAnsicht) => void; hoehe?: 34 | 44 }) {
  return (
    <div role="tablist" aria-label="Ansicht" className="flex shrink-0" style={{ marginLeft: 'auto', padding: 3, borderRadius: 20, background: 'var(--fill2)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
      {([['kacheln', 'Kacheln', SquaresFour], ['liste', 'Liste', List]] as const).map(([key, label, IconKomp]) => {
        const an = wert === key;
        return (
          <button key={key} type="button" role="tab" aria-selected={an} aria-label={label} title={label} onClick={() => onChange(key)}
            className="flex items-center justify-center cursor-pointer"
            style={{ width: hoehe === 44 ? 46 : 38, height: hoehe, border: 'none', borderRadius: hoehe / 2, background: an ? 'var(--bg)' : 'transparent', color: an ? 'var(--ink)' : 'var(--ink3)', boxShadow: an ? '0 2px 8px -2px var(--shade)' : 'none' }}>
            <IconKomp weight="bold" size={15} />
          </button>
        );
      })}
    </div>
  );
}

/** Suchfeld der Filterzeile (42 px, Glas); breit = 46 px über die freie Zeilenbreite (Zähler). */
export function ListenSuche({ wert, onChange, platzhalter, ariaLabel, breit = false }: {
  wert: string; onChange: (v: string) => void; platzhalter: string; ariaLabel: string; breit?: boolean;
}) {
  const stil: React.CSSProperties = breit
    ? { flex: '1 1 260px', minWidth: 0, height: 46, borderRadius: 23, gap: 10, padding: '0 16px' }
    : { flex: '1 1 240px', maxWidth: 340, height: 42, borderRadius: 21, gap: 8, padding: '0 14px' };
  return (
    <label className="immo-glas flex items-center" style={stil}>
      <MagnifyingGlass size={breit ? 17 : 16} color="var(--ink3)" aria-hidden />
      <input value={wert} onChange={e => onChange(e.target.value)} placeholder={platzhalter} aria-label={ariaLabel}
        className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--ink)' }} />
    </label>
  );
}

/** Ring mit Prozentwert (Portal-Quote, Belegung). */
export function AnteilRing({ prozent, groesse = 70, farbe = 'var(--ok)', spur = 'var(--fill3)', text }: {
  prozent: number | null; groesse?: number; farbe?: string; spur?: string; text?: React.ReactNode;
}) {
  const umfang = 2 * Math.PI * 50;
  const p = prozent === null ? 0 : Math.max(0, Math.min(100, prozent));
  return (
    <div className="relative shrink-0" style={{ width: groesse, height: groesse }}>
      <svg width={groesse} height={groesse} viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }} aria-hidden>
        <circle cx="60" cy="60" r="50" fill="none" stroke={spur} strokeWidth="14" />
        {p > 0 && <circle cx="60" cy="60" r="50" fill="none" stroke={farbe} strokeWidth="14" strokeLinecap="round" strokeDasharray={`${(umfang * p) / 100} ${umfang}`} />}
      </svg>
      <span className="immo-zahl absolute inset-0 flex items-center justify-center" style={{ fontSize: groesse >= 100 ? 22 : 15, fontWeight: 800 }}>
        {text ?? (prozent === null ? '–' : `${Math.round(p)}%`)}
      </span>
    </div>
  );
}
